import React from 'react';
import { ArrowRight, Eye, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { motion } from 'framer-motion';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.15, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: (i = 0) => ({
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const steps = [
  { label: 'Sadar', sub: 'menyadari diri' },
  { label: 'Belajar', sub: 'mencari makna' },
  { label: 'Refleksi', sub: 'merenung jujur' },
  { label: 'Dialog', sub: 'berbagi & bertanya' },
  { label: 'Bertumbuh', sub: 'menjadi' },
];

const HeroSection = ({ onRegisterClick, onLoginClick }) => {
  return (
    <section id="beranda" className="relative min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 pt-16 overflow-hidden flex items-center">
      {/* Animated Background Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 right-10 w-[500px] h-[500px] bg-blue-200/40 rounded-full blur-3xl"
          animate={{
            x: [0, 30, -20, 0],
            y: [0, -20, 15, 0],
            scale: [1, 1.05, 0.95, 1],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-10 left-10 w-[400px] h-[400px] bg-indigo-200/30 rounded-full blur-3xl"
          animate={{
            x: [0, -25, 20, 0],
            y: [0, 25, -15, 0],
            scale: [1, 0.95, 1.05, 1],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-purple-100/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.2, 0.35, 0.2],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1.5 h-1.5 bg-blue-400/30 rounded-full"
            style={{
              left: `${15 + i * 15}%`,
              top: `${20 + (i % 3) * 25}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.6, 0.2],
            }}
            transition={{
              duration: 4 + i * 0.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.8,
            }}
          />
        ))}
      </div>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 text-center">
        {/* Badge */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0}
          className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-blue-100 rounded-full px-5 py-2.5 text-blue-700 text-sm font-medium mb-8 shadow-sm"
        >
          <Eye className="w-4 h-4" />
          Pendidikan Berbasis Kesadaran
          <Sparkles className="w-3.5 h-3.5 text-amber-500" />
        </motion.div>

        {/* Headline */}
        <motion.h1
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={1}
          className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6 tracking-tight"
        >
          Dari tahu menjadi{' '}
          <span className="relative inline-block">
            <span className="relative z-10">sadar</span>
            <motion.span
              className="absolute bottom-1 left-0 w-full h-3 bg-blue-200/60 rounded-sm -z-0"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.8, ease: 'easeOut' }}
              style={{ originX: 0 }}
            />
          </span>.
          <br />
          <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Dari sadar, menemukan kembali fitrah.
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={2}
          className="text-lg md:text-xl text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed"
        >
          Akademia Fitrah adalah ruang bagi guru dan orang tua yang ingin memahami fitrah anak — bukan mencetak, tapi menumbuhkan. Perjalanan dimulai dari kesadaran diri Anda sendiri.
        </motion.p>

        {/* Learning Path Steps */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={3}
          className="flex items-center justify-center gap-1.5 sm:gap-2 md:gap-3 text-sm mb-16 flex-wrap"
        >
          {steps.map((step, i) => (
            <React.Fragment key={step.label}>
              {i > 0 && (
                <motion.div
                  variants={scaleIn}
                  initial="hidden"
                  animate="visible"
                  custom={4 + i}
                  className="text-gray-300"
                >
                  <ArrowRight className="w-3.5 h-3.5 hidden sm:block" />
                  <span className="sm:hidden text-xs">&rarr;</span>
                </motion.div>
              )}
              <motion.span
                variants={scaleIn}
                initial="hidden"
                animate="visible"
                custom={4 + i}
                whileHover={{ scale: 1.08, y: -2 }}
                className="relative group bg-white/80 backdrop-blur-sm text-blue-700 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-medium border border-blue-100 shadow-sm cursor-pointer transition-colors hover:bg-blue-50 hover:border-blue-200"
              >
                {step.label}
                <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none italic">
                  {step.sub}
                </span>
              </motion.span>
            </React.Fragment>
          ))}
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={5}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
            <Button
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold text-base px-8 py-6 rounded-xl shadow-lg shadow-blue-600/25 transition-all duration-300 w-full sm:w-auto"
              onClick={onRegisterClick}
            >
              Mulai Perjalanan
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
          <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-gray-200 text-gray-700 hover:bg-white font-semibold text-base px-8 py-6 rounded-xl transition-all duration-300 bg-white/50 backdrop-blur-sm w-full sm:w-auto"
              onClick={onLoginClick}
            >
              Sudah Punya Akun? Masuk
            </Button>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="mt-16 flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="w-6 h-10 rounded-full border-2 border-gray-300 flex justify-center pt-2"
          >
            <motion.div className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
